import { useEffect, useState } from 'react';
import type { GameSocketApi } from './hooks/useGameSocket';
import {
  useGameStore,
  useRoomCode,
  useSnapshot,
} from './store/gameStore';
import { MAX_PLAYERS } from '../../shared/types';
import DrawingBackground from './components/DrawingBackground';
import { GuestBadge } from './components/GuestBadge';
import { ACCOUNT_URL, ensureSession, renameGuest, SessionApiError, type SessionInfo } from './auth/session';

export default function Lobby({ socket }: { socket: GameSocketApi }) {
  const connected = useGameStore((s) => s.connected);
  const myId = useGameStore((s) => s.myPlayerId);
  const snapshot = useSnapshot();
  const roomCode = useRoomCode();
  const error = useGameStore((s) => s.error);

  const [joinCode, setJoinCode] = useState('');

  const inRoom = !!snapshot && !!myId && snapshot.players.some((p) => p.id === myId);

  if (!inRoom) {
    return <Home joinCode={joinCode} setJoinCode={setJoinCode} socket={socket} connected={connected} error={error} />;
  }

  return (
    <RoomLobby
      socket={socket}
      roomCode={roomCode}
      snapshot={snapshot!}
      myId={myId!}
      error={error}
    />
  );
}

function Home({
  joinCode,
  setJoinCode,
  socket,
  connected,
  error,
}: {
  joinCode: string;
  setJoinCode: (v: string) => void;
  socket: GameSocketApi;
  connected: boolean;
  error: string | null;
}) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [guestName, setGuestName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Read back the identity the socket already authenticated with, so the player
  // can see which name they will be seated under.
  useEffect(() => {
    let active = true;
    void ensureSession().then((resolved) => { if (active) setSession(resolved); });
    return () => { active = false; };
  }, []);

  const nameChanged =
    session?.kind === 'guest' &&
    guestName.trim().length > 0 &&
    guestName.trim() !== (session.name ?? '');
  const identityReady =
    !!session?.name && connected && !nameSaving;

  const saveGuestName = async () => {
    if (session?.kind !== 'guest' || !nameChanged || nameSaving) return;
    const candidate = guestName.trim();
    if (!/^[A-Za-z0-9_]{3,20}$/.test(candidate)) {
      setNameError('Use 3–20 letters, numbers, or underscores.');
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      const updated = await renameGuest(candidate);
      setSession(updated);
      await socket.refreshSession();
      setGuestName('');
    } catch (saveError) {
      if (saveError instanceof SessionApiError && saveError.status === 401) {
        const refreshed = await ensureSession();
        if (refreshed) {
          setSession(refreshed);
          setGuestName('');
          setNameError('Your session expired. A new guest identity was created.');
          return;
        }
      }
      setNameError(
        saveError instanceof Error ? saveError.message : 'Could not change name',
      );
    } finally {
      setNameSaving(false);
    }
  };

  const start = () => {
    if (!identityReady) return;
    socket.createRoom();
  };
  const join = () => {
    if (joinCode.trim().length < 6 || !identityReady) return;
    socket.joinRoom(joinCode.trim().toUpperCase());
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-scr-bg to-[#e9faf3] px-6 py-10">
      {/* ── Decorative drawing background ── */}
      <DrawingBackground />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-scr-accent shadow-[0_14px_34px_-12px_rgba(16,185,129,0.6)]">
            <span className="text-4xl">✏️</span>
          </div>
          <h1 className="mt-5 font-display text-6xl font-black tracking-tight text-scr-ink">
            Pictionary
          </h1>
          <p className="mt-2 text-sm font-semibold uppercase tracking-[0.18em] text-scr-accent">
            Draw it · guess it
          </p>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-scr-ink/55">
            One player sketches a secret word while everyone races to guess it.
            Create a room, grab your friends, and draw your way to the top score.
          </p>
        </div>

        <div className="rounded-3xl border border-scr-border bg-white p-7 shadow-[0_4px_24px_rgba(20,23,58,0.08)]">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600">
              {error}
            </div>
          )}
          {!connected && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-700">
              Connecting to server…
            </div>
          )}

          <label className="block text-xs font-bold uppercase tracking-widest text-scr-ink/50">
            Playing as
          </label>
          <div className="mt-1.5 rounded-xl border border-scr-border bg-scr-bg p-3">
            {session?.kind === 'guest' ? (
              <>
                <div className="flex items-center gap-2">
                  <input
                    aria-label="Guest name"
                    value={guestName}
                    onChange={(event) => {
                      setGuestName(event.target.value);
                      setNameError(null);
                    }}
                    minLength={3}
                    maxLength={20}
                    placeholder={session.name ?? ''}
                    disabled={nameSaving}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') void saveGuestName();
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-scr-border bg-white px-3 py-2 text-sm font-semibold text-scr-ink outline-none transition focus:border-scr-accent focus:ring-2 focus:ring-scr-accent/20 disabled:opacity-60"
                  />
                  <GuestBadge />
                  <button
                    type="button"
                    onClick={() => void saveGuestName()}
                    disabled={!nameChanged || nameSaving}
                    className="rounded-lg bg-scr-accent px-3 py-2 text-xs font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {nameSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-xs text-scr-ink/40">
                    3–20 letters, numbers, or underscores
                  </span>
                  <a
                    href={ACCOUNT_URL}
                    className="flex-shrink-0 text-xs font-bold text-scr-accent underline-offset-2 hover:underline"
                  >
                    Sign in
                  </a>
                </div>
              </>
            ) : session?.needsUsername ? (
              <div className="flex items-center justify-between gap-3 px-1 py-1">
                <span className="text-sm text-scr-ink/50">Choose a username to play</span>
                <a
                  href={ACCOUNT_URL}
                  className="flex-shrink-0 text-xs font-bold text-scr-accent underline-offset-2 hover:underline"
                >
                  Choose name
                </a>
              </div>
            ) : session?.name ? (
              <div className="flex items-center justify-between gap-3 px-1 py-1">
                <span className="truncate text-sm font-semibold text-scr-ink">
                  {session.name}
                </span>
                <a
                  href={ACCOUNT_URL}
                  className="flex-shrink-0 text-xs font-bold text-scr-accent underline-offset-2 hover:underline"
                >
                  Account
                </a>
              </div>
            ) : (
              <span className="text-sm text-scr-ink/40">Loading…</span>
            )}
          </div>
          {nameError && (
            <p className="mt-2 text-xs font-semibold text-red-600">{nameError}</p>
          )}

          <button
            onClick={start}
            disabled={!identityReady}
            className="mt-4 w-full rounded-xl bg-scr-accent py-3.5 text-sm font-bold text-white shadow-lg shadow-scr-accent/25 transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Create game room
          </button>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-scr-border" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-scr-ink/35">
              or join
            </span>
            <span className="h-px flex-1 bg-scr-border" />
          </div>

          <div className="flex gap-2">
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="CODE"
              className="min-w-0 flex-1 rounded-xl border border-scr-border bg-scr-bg px-4 py-3 text-center font-mono text-lg font-bold tracking-[0.35em] text-scr-ink outline-none transition focus:border-scr-accent focus:ring-2 focus:ring-scr-accent/30"
            />
            <button
              onClick={join}
              disabled={!identityReady || joinCode.trim().length < 6}
              className="rounded-xl bg-scr-ink px-6 py-3 text-sm font-bold text-white transition hover:opacity-90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Join
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] font-medium text-scr-ink/40">
          Free · No downloads · Up to {MAX_PLAYERS} players per room
        </p>
      </div>
    </div>
  );
}

function RoomLobby({
  socket,
  roomCode,
  snapshot,
  myId,
  error,
}: {
  socket: GameSocketApi;
  roomCode: string | null;
  snapshot: NonNullable<ReturnType<typeof useSnapshot>>;
  myId: string;
  error: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const me = snapshot.players.find((p) => p.id === myId)!;
  const isHost = snapshot.hostId === myId;
  const readyCount = snapshot.players.filter((p) => p.ready).length;
  const allReady = snapshot.players.length >= 2 && snapshot.players.every((p) => p.ready);
  const config = snapshot.config;

  const copyCode = async () => {
    if (!roomCode) return;
    try {
      await navigator.clipboard.writeText(roomCode);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = roomCode;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-scr-bg to-[#e9faf3] px-6 py-10">
      <div className="mx-auto w-full max-w-2xl">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-scr-ink shadow-sm ring-1 ring-scr-border">
            Room code
            <button
              onClick={copyCode}
              title="Copy room code"
              className="rounded-md bg-scr-accent px-2.5 py-0.5 font-mono text-base font-bold tracking-widest text-white hover:brightness-105"
            >
              {roomCode}
            </button>
            <span className="text-xs font-medium text-scr-ink/40">
              {copied ? 'Copied!' : 'share it'}
            </span>
          </span>
          <h1 className="mt-5 font-display text-4xl font-black tracking-tight text-scr-ink">
            Waiting room
          </h1>
          <p className="mt-1.5 text-sm text-scr-ink/55">
            {snapshot.players.length}/{MAX_PLAYERS} players · {readyCount}/{snapshot.players.length} ready
          </p>
        </div>

        {error && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Players */}
        <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {snapshot.players.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-2xl border border-scr-border bg-white p-4"
            >
              <span
                className="flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold text-white"
                style={{ backgroundColor: p.color }}
              >
                {p.name.slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-sm font-bold text-scr-ink">
                  <span className="truncate">{p.name}</span>
                  {p.kind === 'guest' && <GuestBadge />}
                  {p.id === snapshot.hostId && <span className="text-[10px] font-bold uppercase text-scr-gold">host</span>}
                </p>
                <p className="text-xs text-scr-ink/45">
                  {p.id === myId ? 'you' : p.guessed ? '' : ''} {!p.connected && '· disconnected'}
                </p>
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                  p.ready ? 'bg-scr-accent/15 text-scr-accent-dark' : 'bg-scr-border/60 text-scr-ink/45'
                }`}
              >
                {p.ready ? 'Ready' : 'Waiting'}
              </span>
            </div>
          ))}
        </div>

        {/* Host settings */}
        {isHost && (
          <div className="mt-6 rounded-2xl border border-scr-border bg-white p-5">
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-scr-ink/50">
              Game settings (host)
            </p>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-scr-ink/70">
                Turn time:
                <select
                  value={config.turnSeconds}
                  onChange={(e) => socket.setConfig({ turnSeconds: Number(e.target.value) })}
                  className="rounded-lg border border-scr-border bg-scr-bg px-2 py-1.5 text-sm font-medium outline-none focus:border-scr-accent"
                >
                  {[60, 80, 100, 120].map((s) => (
                    <option key={s} value={s}>{s}s</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-scr-ink/70">
                Rounds each:
                <select
                  value={config.roundsPerPlayer}
                  onChange={(e) => socket.setConfig({ roundsPerPlayer: Number(e.target.value) })}
                  className="rounded-lg border border-scr-border bg-scr-bg px-2 py-1.5 text-sm font-medium outline-none focus:border-scr-accent"
                >
                  {[1, 2, 3, 4].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            onClick={() => socket.setReady(!me.ready)}
            className={`flex-1 rounded-xl py-3.5 text-sm font-bold transition ${
              me.ready
                ? 'bg-amber-400 text-white shadow-lg shadow-amber-400/30 hover:brightness-105'
                : 'bg-scr-accent text-white shadow-lg shadow-scr-accent/25 hover:brightness-105'
            }`}
          >
            {me.ready ? 'Unready' : "I'm ready"}
          </button>
          {isHost && (
            <button
              onClick={() => socket.startGame()}
              disabled={!allReady}
              className="flex-1 rounded-xl bg-scr-ink py-3.5 text-sm font-bold text-white transition hover:opacity-90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-30"
            >
              Start game
            </button>
          )}
          <button
            onClick={() => socket.leave()}
            className="rounded-xl border border-scr-border bg-white px-6 py-3.5 text-sm font-bold text-scr-ink/50 transition hover:text-red-600"
          >
            Leave
          </button>
        </div>

        {!allReady && snapshot.players.length >= 2 && (
          <p className="mt-4 text-center text-xs font-medium text-scr-ink/45">
            Wait for everyone to hit “I'm ready”.
          </p>
        )}
      </div>
    </div>
  );
}
